import React, { useCallback, useMemo } from "react";
import ActionPricingModal, {
	ActionPricingModalProps,
} from "../actionModal/ActionPricingModal";
import { GenerateCurtailmentObjects } from "../../../../common/BuildCurtailmentObjectsComponents/GenerateCurtailmentObjects";
import { SupportedCountry } from "../../../../common/constants/countries";
import { useSelector } from "react-redux";
import { selectFeatureFlags } from "../../../parameters/ParametersPage.selector";
import * as _ from "lodash";

export interface MarkAsWonModalProps extends ActionPricingModalProps {}

export enum MarkAsWonModalSteps {
	REGULAR_FIELDS = 0,
	CCO_FIELDS = 1,
}

export function MarkAsWonModal(props: MarkAsWonModalProps) {
	const featureFlags = useSelector(selectFeatureFlags);
	const [step, setStep] = React.useState(MarkAsWonModalSteps.REGULAR_FIELDS);

	const [intermediateState, setIntermediateState] = React.useState<any>({});
	const country = useMemo(
		() => props.forPricings?.[0]?.countries_list?.[0] as SupportedCountry,
		[props.forPricings]
	);
	const exisitingValues = useMemo(
		() => props.forPricings?.[0]?.cco_build_input,
		[props.forPricings?.[0]]
	);
	const ccoField = useMemo(
		() =>
			_.first(
				_.values(props.action?.fields || {}).filter(
					(field) => field.name === "cco_build_input"
				)
			),
		[props.action]
	);
	const canBuildCCOs = useMemo(() => {
		if (!ccoField || ccoField?.disabled) {
			return false;
		}
		return featureFlags?.enable_coo_building_france
			? [SupportedCountry.FRANCE, SupportedCountry.GERMANY].includes(
					country
			  )
			: country === SupportedCountry.GERMANY;
	}, [country, featureFlags, ccoField]);

	const onClose = useCallback(() => {
		setStep(MarkAsWonModalSteps.REGULAR_FIELDS);
		props.onClose();
	}, [props]);

	const submitFirstStep = useCallback(
		(values: any) => {
			if (canBuildCCOs) {
				setIntermediateState(values);
				setStep(MarkAsWonModalSteps.CCO_FIELDS);
			} else {
				props.onSubmit(values);
			}
		},
		[canBuildCCOs, setIntermediateState, setStep, props.onSubmit]
	);

	const submitSecondStep = useCallback(
		(values: any) => {
			props.onSubmit({
				...intermediateState,
				ccoBuildInput: values,
			});
		},
		[props.onSubmit, intermediateState, country]
	);

	const onSkip = useCallback(() => {
		props.onSubmit({
			...intermediateState,
			ccoBuildInput: null,
		});
	}, [props.onSubmit, intermediateState]);

	if (step === MarkAsWonModalSteps.REGULAR_FIELDS) {
		return (
			<ActionPricingModal
				{...props}
				onSubmit={submitFirstStep}
				onClose={onClose}
			/>
		);
	}

	return (
		<GenerateCurtailmentObjects
			isOpen={true}
			onClose={onClose}
			onSubmit={submitSecondStep}
			country={country}
			existingValues={exisitingValues}
			onSkip={onSkip}
		/>
	);
}
